import React, { useEffect, useState } from 'react';
import { Grid, TextField, Button, Link, Typography, FormControl, InputLabel, Select, MenuItem, Divider, Alert, IconButton, InputAdornment, CircularProgress, Avatar, Box } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { frFR, enUS } from '@mui/x-date-pickers/locales';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';

import ReactPhoneInput from '../vendor/reactPhoneInput';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


import TivityLogo from './TivityLogo';
import SocialLoginButtons from './SocialLoginButtons';
import RequestSentScreen from './RequestSentScreen';
import AuthLayout from './AuthLayout';
import { useSignupMutation, useLoginWithFirebaseMutation, useGetLogoPathQuery } from '../api/apiSlice';
import { useCompleteLogin } from '../hooks/useCompleteLogin';
import { getAuthErrorMessage } from '../utils/authError';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';

function Signup() {
  const { t, i18n } = useTranslation();
  const pickersLocaleText = (i18n.language === 'en' ? enUS : frFR).components.MuiLocalizationProvider.defaultProps.localeText;
  const [searchParams] = useSearchParams();
  const clubId = searchParams.get('clubId') ?? '';
  const clubName = searchParams.get('clubName') ?? '';
  const invite = searchParams.get('invite') ?? undefined;

  const [name, setName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [sexe, setSexe] = useState('');
  const [birthday, setBirthday] = useState<Dayjs>(dayjs(new Date()));
  // Invite links generated in ClubSettings carry the invited email as a
  // convenience prefill -- the invite itself is redeemable with any email
  // or signup method, not just this one (see auth.service.ts).
  const [email, setEmail] = useState(() => searchParams.get('email') ?? '');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordCheck, setPasswordCheck] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordCheck, setShowPasswordCheck] = useState(false);
  const [error, setError] = useState('');
  const [requestSent, setRequestSent] = useState(false);

  const { data: logoRows } = useGetLogoPathQuery(clubId, { skip: !clubId });
  const logoPath = logoRows?.[0]?.LOGO;

  const navigate = useNavigate();
  const [signup, { isLoading: isSigningUp }] = useSignupMutation();
  const [loginWithFirebase, { isLoading: isFirebaseSigningUp }] = useLoginWithFirebaseMutation();
  const completeLogin = useCompleteLogin();

  useEffect(() => {
    if (!clubId) {
      navigate('/clubs', { replace: true });
    }
  }, [clubId, navigate]);

  function validateUserData(userData: Record<string, unknown>) {
    for (const key in userData) {
      if (!userData[key]) {
        // If any value is empty, return false
        return false;
      }
    }
    // If all values are not empty, return true
    return true;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if the password and password check match
    if (password !== passwordCheck) {
      setError(t('signup.passwordMismatch'));
      return;
    }

    const birthdayString = birthday.toISOString().slice(0, 19).replace('T', ' ');

    // Create an object with the user's data
    const requiredFields = { clubId, name, firstName, email, phone, sexe, birthdayString, password };

    if (validateUserData(requiredFields)) {
      try {
        const token = await signup({ ...requiredFields, invite }).unwrap();

        // Reset the error state on successful registration
        setError('');
        const { pending } = completeLogin(token);
        setRequestSent(pending);
      } catch (error: any) {
        setError(getAuthErrorMessage(error, t, 'signup.registrationFailed'));
      }
    }
    else {
      setError(t('signup.fillAllFields'));
    }
  };

  const handleSocialSignup = async (signIn: () => Promise<string>) => {
    setError('');

    let idToken: string;
    try {
      idToken = await signIn();
    } catch {
      setError(t('common.genericError'));
      return;
    }

    try {
      const token = await loginWithFirebase({ idToken, clubId: Number(clubId), invite }).unwrap();
      const { pending } = completeLogin(token);
      setRequestSent(pending);
    } catch (error: any) {
      setError(getAuthErrorMessage(error, t));
    }
  };

  const updatePhone = (value: string) => {
      setPhone(value);
  };

  if (!clubId) {
    return null;
  }

  if (requestSent) {
    return <RequestSentScreen clubName={clubName} ctaLabel={t('signup.goToLogin')} ctaHref="/login" />;
  }

  const isSubmitting = isSigningUp || isFirebaseSigningUp;

  return (
    <AuthLayout>
      <Grid container spacing={3} alignItems="center">
        <Grid item xs={12} sx={{ textAlign: "center" }}>
          <Link href="/" underline="none" sx={{ display: 'inline-flex', justifyContent: 'center' }}>
            <TivityLogo />
          </Link>
        </Grid>
        {clubName && (
          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
              <Avatar
                src={logoPath || PLACEHOLDER_IMAGE}
                onError={handleImageError}
                alt={clubName}
                variant="rounded"
                sx={{ width: 32, height: 32 }}
              />
              <Typography variant="body2" color="text.secondary">
                {t('signup.joiningClub', { clubName })}
              </Typography>
            </Box>
          </Grid>
        )}
        {error && (
          <Grid item xs={12}>
            <Alert severity="error">{error}</Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <SocialLoginButtons onProviderSelect={handleSocialSignup} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider>
            <Typography variant="body2" color="text.secondary">
              {t('login.orDivider')}
            </Typography>
          </Divider>
        </Grid>
        <Grid item xs={12}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="name"
                  label={t('signup.lastName')}
                  variant="outlined"
                  fullWidth
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="firstName"
                  label={t('signup.firstName')}
                  variant="outlined"
                  fullWidth
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined">
                      <InputLabel htmlFor="Sexe">{t('signup.sexe')}</InputLabel>
                      <Select
                          label={t('signup.sexe')}
                          id="Sexe"
                          value={sexe}
                          onChange={(e) => setSexe(e.target.value)}
                          >
                          <MenuItem value="Homme">{t('signup.male')}</MenuItem>
                          <MenuItem value="Femme">{t('signup.female')}</MenuItem>
                          <MenuItem value="Autre">{t('signup.other')}</MenuItem>
                      </Select>
                  </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language} localeText={pickersLocaleText}>
                          <DatePicker
                              label={t('signup.birthday')}
                              format="DD-MM-YYYY"
                              value={birthday}
                              onChange={(newValue: Dayjs | null) => newValue && setBirthday(newValue)}
                          />
                  </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="email"
                  type="email"
                  label={t('signup.email')}
                  variant="outlined"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={error !== ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ReactPhoneInput
                  label={t('signup.mobile')}
                  variant="outlined"
                  country={'fr'}
                  value={phone}
                  onChange={updatePhone}
                  enableSearch
                  component={TextField}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  label={t('signup.password')}
                  variant="outlined"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                              <IconButton
                                  aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                                  onClick={() => setShowPassword((show) => !show)}
                                  edge="end"
                              >
                                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                          </InputAdornment>
                      ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  id="password-check"
                  type={showPasswordCheck ? 'text' : 'password'}
                  label={t('signup.confirmPassword')}
                  variant="outlined"
                  fullWidth
                  required
                  value={passwordCheck}
                  onChange={(e) => setPasswordCheck(e.target.value)}
                  error={error !== ''}
                  InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                              <IconButton
                                  aria-label={showPasswordCheck ? t('login.hidePassword') : t('login.showPassword')}
                                  onClick={() => setShowPasswordCheck((show) => !show)}
                                  edge="end"
                              >
                                  {showPasswordCheck ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                          </InputAdornment>
                      ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  id="signup-btn"
                  variant="contained"
                  color="primary"
                  fullWidth
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                >
                  {t('signup.submit')}
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Link href="/login" variant="body2">
                  {t('signup.hasAccount')}
                </Link>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </AuthLayout>
  );
}

export default Signup;
